/**
 * SignatureBlockSelector
 * Seletor de duas listas do bloco de assinaturas: disponíveis à esquerda,
 * adicionados à direita, com os botões Adicionar e Remover — o mesmo padrão já
 * usado na aplicação de riscos e exames.
 *
 * Duas particularidades que vêm da decisão de bloco misto:
 * 1. A lista da esquerda traz profissionais E declarações juntos, distinguidos
 *    visualmente (profissional mostra conselho e registro; declaração, o título
 *    do texto).
 * 2. A ordem da lista da direita É a ordem de impressão — por isso ela é
 *    reordenável, e não apenas um conjunto.
 *
 * A ART (#345) só aparece na lista da DIREITA: é escolha sobre o que vai sair
 * impresso, e a esquerda é catálogo. Vencida entra na lista marcada e
 * selecionável — quem só tem vencida precisa poder emitir com ela, e o backend
 * responde com `ART_VENCIDA` como AVISO, não bloqueio.
 *
 * @version 2026.1.0
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import {
  ArrowBack as RemoveIcon,
  ArrowDownward as DownIcon,
  ArrowForward as AddIcon,
  ArrowUpward as UpIcon,
  OpenInNew as AbreEmOutraAbaIcon,
} from '@mui/icons-material';
import documentService from '../../../services/documentService';
import { useAcao } from '../../../authorization/useVeredito';
import type {
  AvailableSignatory,
  AvailableSignatureItems,
  ReportType,
  SignatoryArt,
  SignatureBlockItem,
} from '../../../types/document';

/** Forma única para profissional e declaração, para as duas listas serem homogêneas. */
interface BlockOption {
  key: string;
  item: SignatureBlockItem;
  primary: string;
  secondary: string;
  kind: 'signatory' | 'declaration';
  /** Vazio para declaração e para profissional sem nenhuma ART cadastrada. */
  arts: SignatoryArt[];
  /** `companyResponsibleId`, para casar a troca de ART com o item em `value`. */
  responsibleId?: number;
  /** O profissional dono da ART — só ele tem tela de cadastro de ART. */
  professionalId?: number;
}

interface SignatureBlockSelectorProps {
  companyId: number | null;
  type: ReportType | '';
  /** Itens já escolhidos, NA ORDEM de impressão. */
  value: SignatureBlockItem[];
  /**
   * Precisa ser estável entre renders (o setter do `useState` do pai serve): ele
   * entra nas dependências do efeito que carrega a lista, e um callback recriado
   * a cada render refaria a requisição sem parar.
   */
  onChange: (items: SignatureBlockItem[]) => void;
  /**
   * Muda quando a pessoa volta de outra aba onde foi resolver algo (#604): a lista é
   * recarregada mantendo o que estava escolhido e ainda existe. Zero, ou ausente, não recarrega.
   */
  recarregarEm?: number;
  /** Avisado quando a pessoa abre o cadastro de ART, que vai para outra aba. */
  onAbrirLink?: () => void;
}

const chaveDoItem = (item: SignatureBlockItem): string =>
  item.kind === 'signatory'
    ? `signatory:${item.companyResponsibleId}`
    : `declaration:${item.standardTextId}`;

const mensagemDeErro = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback;

/**
 * As DUAS coisas, e não uma escolha entre elas: o rótulo impresso é o que a
 * pessoa precisa conferir antes de gerar o documento, e conselho e registro são
 * o que distingue dois profissionais de mesmo nome na hora de escolher quem
 * assina. Quem não tem registro cadastrado fica só com o rótulo.
 *
 * O VÍNCULO entra só quando o nome se repete na lista — a mesma pessoa é
 * responsável médica e técnica da empresa, por exemplo. Sem ele, as duas linhas
 * ficariam idênticas quando falta o registro, e escolher a errada é o que faz o
 * documento sair acusando falta de médico responsável. Fora desse caso ele é
 * ruído: é classificação interna, e não sai impresso.
 */
const identificacaoDe = (assinante: AvailableSignatory, nomeRepetido: boolean): string => {
  const registro = `${assinante.council} ${assinante.registration}`.trim();

  return [assinante.printedRole, registro, nomeRepetido ? assinante.roleLabel : '']
    .filter((parte) => parte !== '')
    .join(' · ');
};

const nomesRepetidos = (assinantes: AvailableSignatory[]): Set<string> => {
  const vistos = new Set<string>();
  const repetidos = new Set<string>();

  assinantes.forEach((assinante) => {
    if (vistos.has(assinante.name)) {
      repetidos.add(assinante.name);
      return;
    }

    vistos.add(assinante.name);
  });

  return repetidos;
};

/** Profissionais e declarações na forma única das duas listas. */
const montarOpcoes = (itens: AvailableSignatureItems): BlockOption[] => {
  const repetidos = nomesRepetidos(itens.signatories);
  const profissionais: BlockOption[] = itens.signatories.map((assinante) => ({
    key: `signatory:${assinante.companyResponsibleId}`,
    item: { kind: 'signatory', companyResponsibleId: assinante.companyResponsibleId },
    primary: assinante.name,
    secondary: identificacaoDe(assinante, repetidos.has(assinante.name)),
    kind: 'signatory',
    arts: assinante.arts,
    responsibleId: assinante.companyResponsibleId,
    ...(assinante.professionalId === undefined
      ? {}
      : { professionalId: assinante.professionalId }),
  }));

  const declaracoes: BlockOption[] = itens.declarations.map((declaracao) => ({
    key: `declaration:${declaracao.standardTextId}`,
    item: { kind: 'declaration', standardTextId: declaracao.standardTextId },
    primary: declaracao.title,
    secondary: 'Declaração',
    kind: 'declaration',
    arts: [],
  }));


  return [...profissionais, ...declaracoes];
};

/**
 * O que continua valendo do bloco escolhido depois de recarregar a lista: some só o item que
 * deixou de existir, e a ART escolhida que sumiu volta para a automática — o profissional fica.
 */
const manterOQueAindaExiste = (
  escolhidos: SignatureBlockItem[],
  opcoes: BlockOption[]
): SignatureBlockItem[] => {
  const porChave = new Map(opcoes.map((opcao) => [opcao.key, opcao]));

  return escolhidos.flatMap<SignatureBlockItem>((item) => {
    const opcao = porChave.get(chaveDoItem(item));
    if (!opcao) return [];
    if (item.kind !== 'signatory' || item.artId === undefined) return [item];
    if (opcao.arts.some((art) => art.id === item.artId)) return [item];

    return [{ kind: 'signatory', companyResponsibleId: item.companyResponsibleId }];
  });
};

/**
 * Mesma regra do backend quando `artId` não vem: a ART válida mais recente. A
 * lista chega ordenada por emissão decrescente, então basta a primeira não
 * vencida. Sem nenhuma válida, nada fica pré-selecionado — escolher uma vencida
 * é decisão explícita do usuário, e é ela que dispara o aviso `ART_VENCIDA`.
 */
const artAutomatica = (arts: SignatoryArt[]): SignatoryArt | undefined =>
  arts.find((art) => !art.expired);

/**
 * A ART se cadastra na FICHA do profissional (`/professionals/:id`) — saiu da
 * tela de edição em #345. Aqui a rota é montada no cliente porque é o cliente
 * que conhece o item selecionado; na PENDÊNCIA da prévia quem manda a rota é o
 * backend, que é quem sabe de qual signatário ela é.
 */
const rotaDeCadastroDeArt = (professionalId: number): string =>
  `/professionals/${professionalId}`;

/** Mesma permissão que `POST /professionals/:id/arts` exige. */
const PERMISSAO_CADASTRO_DE_ART = 'professional.update';

export const SignatureBlockSelector: React.FC<SignatureBlockSelectorProps> = ({
  companyId,
  type,
  value,
  onChange,
  recarregarEm = 0,
  onAbrirLink,
}) => {
  const cadastrarArt = useAcao(PERMISSAO_CADASTRO_DE_ART, 'Cadastrar ART');
  const [carregadas, setCarregadas] = useState<BlockOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableSelection, setAvailableSelection] = useState<string[]>([]);
  const [chosenSelection, setChosenSelection] = useState<string[]>([]);

  /**
   * Sem empresa ou sem tipo não há lista — derivado, e não zerado por `setState`
   * dentro do efeito, que dispararia um render a mais a cada limpeza.
   */
  const options = useMemo(
    () => (companyId === null || type === '' ? [] : carregadas),
    [companyId, type, carregadas]
  );

  useEffect(() => {
    if (companyId === null || type === '') {
      return;
    }

    let ativo = true;

    const carregar = async (): Promise<void> => {
      setLoading(true);
      setError(null);

      try {
        const itens = await documentService.listSignatureItems(companyId, type);
        if (!ativo) return;

        setCarregadas(montarOpcoes(itens));

        // Os papéis previstos pelo tipo já entram no bloco — é o caso comum, e
        // deixá-los de fora faria toda emissão começar com uma pendência.
        const sugeridos = itens.signatories
          .filter((assinante) => assinante.suggested)
          .map<SignatureBlockItem>((assinante) => ({
            kind: 'signatory',
            companyResponsibleId: assinante.companyResponsibleId,
          }));

        if (sugeridos.length > 0) {
          onChange(sugeridos);
        }
      } catch (err: unknown) {
        if (ativo) {
          setError(mensagemDeErro(err, 'Não foi possível carregar o bloco de assinaturas'));
        }
      } finally {
        if (ativo) setLoading(false);
      }
    };

    void carregar();

    return () => {
      ativo = false;
    };
  }, [companyId, type, onChange]);

  // Ref, e não dependência: o recarregamento lê o bloco do momento, mas não roda a cada escolha.
  const escolhidosAtuais = useRef(value);
  const ultimoRecarregamento = useRef(recarregarEm);

  useEffect(() => {
    escolhidosAtuais.current = value;
  });

  /**
   * Separado da carga inicial porque ela repõe os papéis sugeridos — fazer isso na volta
   * desfaria o que a pessoa escolheu antes de sair para cadastrar.
   */
  useEffect(() => {
    if (recarregarEm === ultimoRecarregamento.current) return;
    ultimoRecarregamento.current = recarregarEm;
    if (companyId === null || type === '') return;

    let ativo = true;

    documentService
      .listSignatureItems(companyId, type)
      .then((itens) => {
        if (!ativo) return;
        const opcoes = montarOpcoes(itens);
        setCarregadas(opcoes);

        const mantidos = manterOQueAindaExiste(escolhidosAtuais.current, opcoes);
        const mudou =
          mantidos.length !== escolhidosAtuais.current.length ||
          mantidos.some((item, indice) => item !== escolhidosAtuais.current[indice]);
        if (mudou) onChange(mantidos);
      })
      // Sem o recarregamento a lista de antes continua utilizável, e a emissão confere de novo.
      .catch(() => undefined);

    return () => {
      ativo = false;
    };
  }, [recarregarEm, companyId, type, onChange]);

  const porChave = useMemo(
    () => new Map(options.map((opcao) => [opcao.key, opcao])),
    [options]
  );

  const chosenKeys = useMemo(() => value.map(chaveDoItem), [value]);

  const available = useMemo(
    () => options.filter((opcao) => !chosenKeys.includes(opcao.key)),
    [options, chosenKeys]
  );

  /** A direita segue a ordem de `value`, não a de `options`. */
  const chosen = useMemo(
    () =>
      chosenKeys
        .map((chave) => porChave.get(chave))
        .filter((opcao): opcao is BlockOption => opcao !== undefined),
    [chosenKeys, porChave]
  );

  const alternar = (chave: string, selecao: string[], set: (chaves: string[]) => void): void => {
    set(selecao.includes(chave) ? selecao.filter((atual) => atual !== chave) : [...selecao, chave]);
  };

  const adicionar = useCallback((): void => {
    const novos = available
      .filter((opcao) => availableSelection.includes(opcao.key))
      .map((opcao) => opcao.item);

    if (novos.length === 0) return;

    onChange([...value, ...novos]);
    setAvailableSelection([]);
  }, [available, availableSelection, onChange, value]);

  const remover = useCallback((): void => {
    onChange(value.filter((item) => !chosenSelection.includes(chaveDoItem(item))));
    setChosenSelection([]);
  }, [chosenSelection, onChange, value]);

  /** ART escolhida hoje para cada signatário do bloco, por `companyResponsibleId`. */
  const artEscolhida = useMemo(() => {
    const mapa = new Map<number, number | undefined>();

    value.forEach((item) => {
      if (item.kind === 'signatory') {
        mapa.set(item.companyResponsibleId, item.artId);
      }
    });

    return mapa;
  }, [value]);

  const trocarArt = useCallback(
    (responsibleId: number, artId: number): void => {
      onChange(
        value.map((item) =>
          item.kind === 'signatory' && item.companyResponsibleId === responsibleId
            ? { ...item, artId }
            : item
        )
      );
    },
    [onChange, value]
  );

  /** Move um único item; com mais de um selecionado a ordem pretendida é ambígua. */
  const mover = (direcao: -1 | 1): void => {
    if (chosenSelection.length !== 1) return;

    const chave = chosenSelection[0];
    const indice = chosenKeys.indexOf(chave);
    const destino = indice + direcao;

    if (indice < 0 || destino < 0 || destino >= value.length) return;

    const reordenado = [...value];
    const [movido] = reordenado.splice(indice, 1);
    reordenado.splice(destino, 0, movido);
    onChange(reordenado);
  };

  /**
   * A saída para quem ficou sem ART válida. Sem ela o item era beco sem saída:
   * quem não tem nenhuma ART não via nem seletor, e quem só tem vencida via um
   * "Sem ART válida" desabilitado — os dois casos se resolvem CADASTRANDO uma
   * ART, e nada aqui dizia onde.
   *
   * Sem a permissão que a API do cadastro exige, sai o recado em vez do link:
   * levar a uma tela que responderia 403 é pior que não levar a lugar nenhum.
   */
  const renderCaminhoDaArt = (opcao: BlockOption): React.ReactNode => {
    if (opcao.professionalId === undefined) {
      return null;
    }

    if (cadastrarArt.disabled) {
      return (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {`Sem ART válida. ${cadastrarArt.tooltip} Peça a um administrador para cadastrar.`}
        </Typography>
      );
    }

    return (
      <Link
        component="a"
        href={rotaDeCadastroDeArt(opcao.professionalId)}
        target="_blank"
        rel="noopener noreferrer"
        title="Abre em outra aba"
        onClick={onAbrirLink}
        variant="caption"
        underline="always"
        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
      >
        {opcao.arts.length === 0 ? 'Cadastrar ART' : 'Cadastrar renovação da ART'}
        <AbreEmOutraAbaIcon sx={{ fontSize: 12 }} />
      </Link>
    );
  };

  /**
   * O `Select` mora DENTRO do `ListItemButton`, então o clique nele precisa
   * parar de subir: sem isso, abrir o seletor tiraria o item da seleção e o
   * botão Remover mudaria de estado no meio da escolha. O mesmo vale para o
   * link de cadastro, que sai do formulário de emissão.
   */
  const renderSeletorDeArt = (opcao: BlockOption): React.ReactNode => {
    const responsibleId = opcao.responsibleId;

    if (opcao.kind !== 'signatory' || responsibleId === undefined) {
      return null;
    }

    const selecionada =
      opcao.arts.length === 0
        ? ''
        : (artEscolhida.get(responsibleId) ?? artAutomatica(opcao.arts)?.id ?? '');

    const caminho = selecionada === '' ? renderCaminhoDaArt(opcao) : null;

    if (opcao.arts.length === 0 && caminho === null) {
      return null;
    }

    return (
      <Box
        onClick={(evento) => evento.stopPropagation()}
        sx={{ ml: 1, minWidth: 190 }}
      >
        {opcao.arts.length > 0 && (
          <Select<string | number>
            size="small"
            fullWidth
            displayEmpty
            value={selecionada}
            onChange={(evento: SelectChangeEvent<string | number>) =>
              trocarArt(responsibleId, Number(evento.target.value))
            }
            inputProps={{ 'aria-label': `ART de ${opcao.primary}` }}
          >
            {selecionada === '' && (
              <MenuItem value="" disabled>
                Sem ART válida
              </MenuItem>
            )}
            {opcao.arts.map((art) => (
              <MenuItem key={art.id} value={art.id}>
                <Box component="span" sx={{ mr: art.expired ? 1 : 0 }}>
                  {art.number}
                </Box>
                {art.expired && (
                  <Chip size="small" color="warning" label="Vencida" variant="outlined" />
                )}
              </MenuItem>
            ))}
          </Select>
        )}
        {caminho}
      </Box>
    );
  };

  const renderLista = (
    itens: BlockOption[],
    selecao: string[],
    onToggle: (chave: string) => void,
    vazio: string,
    comArt = false
  ): React.ReactNode => (
    <Paper variant="outlined" sx={{ height: 260, overflow: 'auto' }}>
      {itens.length === 0 ? (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {vazio}
          </Typography>
        </Box>
      ) : (
        <List dense disablePadding>
          {itens.map((opcao) => (
            <ListItemButton
              key={opcao.key}
              selected={selecao.includes(opcao.key)}
              onClick={() => onToggle(opcao.key)}
            >
              <ListItemText primary={opcao.primary} secondary={opcao.secondary} />
              {opcao.kind === 'declaration' && (
                <Chip size="small" label="Declaração" variant="outlined" />
              )}
              {comArt && renderSeletorDeArt(opcao)}
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );

  return (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Bloco de assinaturas
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        A ordem da lista da direita é a ordem de impressão no documento.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Disponíveis
            </Typography>
            {renderLista(
              available,
              availableSelection,
              (chave) => alternar(chave, availableSelection, setAvailableSelection),
              'Nada disponível para esta empresa e tipo de documento.'
            )}
          </Box>

          <Stack spacing={1} justifyContent="center" sx={{ minWidth: 140 }}>
            <Button
              variant="outlined"
              size="small"
              endIcon={<AddIcon />}
              disabled={availableSelection.length === 0}
              onClick={adicionar}
            >
              Adicionar
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RemoveIcon />}
              disabled={chosenSelection.length === 0}
              onClick={remover}
            >
              Remover
            </Button>
            <Divider flexItem />
            <Button
              size="small"
              startIcon={<UpIcon />}
              disabled={chosenSelection.length !== 1}
              onClick={() => mover(-1)}
            >
              Subir
            </Button>
            <Button
              size="small"
              startIcon={<DownIcon />}
              disabled={chosenSelection.length !== 1}
              onClick={() => mover(1)}
            >
              Descer
            </Button>
          </Stack>

          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              No documento
            </Typography>
            {renderLista(
              chosen,
              chosenSelection,
              (chave) => alternar(chave, chosenSelection, setChosenSelection),
              'Nenhum item no bloco de assinaturas.',
              true
            )}
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default SignatureBlockSelector;
